import { EmployeeService } from "~/entity/Employee/api/service";
import { LicensesService } from "~/entity/Licenses/api/service";

import {
  ILicenseAssignPayload,
  ILicenseOwner,
  ILicenseRevokePayload,
} from "./types";

const PAGE_SIZE = 100;

export interface IAssignSubmitResult {
  assigned: number;
  failed: number;
}

export interface IRevokeSubmitResult {
  revoked: number;
  failed: number;
}

const fetchAvailableTariffId = async (
  owner: ILicenseOwner,
  instanceId: number,
): Promise<string | null> => {
  const response = await LicensesService.GET.employees({
    dealerId: owner.dealerId,
    organizationId: owner.organizationId,
    instanceId,
    status: "available",
    page: 0,
    count: 1,
  });
  const data = response.data;

  if (data.level !== "employees") {
    return null;
  }

  return data.rows[0]?.tariffId ?? null;
};

const fetchAllEmployeeIds = async (
  owner: ILicenseOwner,
  positionInstanceId: number,
): Promise<number[]> => {
  if (!owner.dealerId && !owner.organizationId) {
    return [];
  }

  const ids: number[] = [];
  let page = 0;

  for (;;) {
    const response = await EmployeeService.GET.list({
      page,
      count: PAGE_SIZE,
      dealerIds: owner.dealerId ? [owner.dealerId] : undefined,
      bindingTypes: owner.organizationId ? ["PARTNER"] : undefined,
      partnerIds: owner.organizationId ? [owner.organizationId] : undefined,
      positionIds: [positionInstanceId],
    });
    const { items, total } = response.data;

    items.forEach((item) => {
      if (
        owner.organizationId &&
        item.organization?.id !== owner.organizationId
      ) {
        return;
      }

      if (item.employee.id != null) {
        ids.push(item.employee.id);
      }
    });

    page += 1;

    if (page * PAGE_SIZE >= total || items.length === 0) {
      break;
    }
  }

  return ids;
};

export const submitLicenseAssign = async (
  payload: ILicenseAssignPayload,
): Promise<IAssignSubmitResult> => {
  let assigned = 0;
  let failed = 0;

  for (const organization of payload.organizations) {
    for (const position of organization.positions) {
      const employeeIds = position.allEmployees
        ? await fetchAllEmployeeIds(
            organization.owner,
            position.positionInstanceId,
          )
        : position.employeeIds;

      if (employeeIds.length === 0) {
        continue;
      }

      const tariffId = await fetchAvailableTariffId(
        organization.owner,
        position.instanceId,
      );

      if (!tariffId) {
        failed += employeeIds.length;
        continue;
      }

      const results = await Promise.all(
        employeeIds.map((employeeId) =>
          LicensesService.POST.assign({
            employeeId,
            dealerId: organization.owner.dealerId,
            organizationId: organization.owner.organizationId,
            tariffId,
            positionInstanceId: position.positionInstanceId,
          }).then(
            () => true,
            () => false,
          ),
        ),
      );

      results.forEach((ok) => {
        if (ok) {
          assigned += 1;
        } else {
          failed += 1;
        }
      });
    }
  }

  return { assigned, failed };
};

const fetchAssignedEmployeeIds = async (
  owner: ILicenseOwner,
  instanceId: number,
): Promise<number[]> => {
  const employeeIds: number[] = [];
  let page = 0;

  for (;;) {
    const response = await LicensesService.GET.employees({
      dealerId: owner.dealerId,
      organizationId: owner.organizationId,
      instanceId,
      status: "assigned",
      page,
      count: PAGE_SIZE,
    });
    const data = response.data;

    if (data.level !== "employees" || data.rows.length === 0) {
      break;
    }

    data.rows.forEach((row) => {
      if (row.employeeId != null) {
        employeeIds.push(row.employeeId);
      }
    });

    page += 1;

    if (page * PAGE_SIZE >= data.total) {
      break;
    }
  }

  return employeeIds;
};

const fetchAssignedEmployeeIdsForOwner = async (
  owner: ILicenseOwner,
): Promise<number[]> => {
  const employeeIds: number[] = [];
  let page = 0;

  for (;;) {
    const response = await LicensesService.GET.positionGroups({
      dealerId: owner.dealerId,
      organizationId: owner.organizationId,
      page,
      count: PAGE_SIZE,
    });
    const data = response.data;

    if (data.level !== "positions" || data.rows.length === 0) {
      break;
    }

    for (let index = 0; index < data.rows.length; index += 1) {
      const row = data.rows[index];

      if (row.instanceId == null || row.assigned === 0) {
        continue;
      }

      const assignedIds = await fetchAssignedEmployeeIds(owner, row.instanceId);
      assignedIds.forEach((id) => {
        employeeIds.push(id);
      });
    }

    page += 1;

    if (page * PAGE_SIZE >= data.total) {
      break;
    }
  }

  return employeeIds;
};

const collectRevokeEmployeeIds = async (
  organization: ILicenseRevokePayload["organizations"][number],
): Promise<number[]> => {
  if (organization.positions.length === 0) {
    return fetchAssignedEmployeeIdsForOwner(organization.owner);
  }

  const employeeIds: number[] = [];

  for (let index = 0; index < organization.positions.length; index += 1) {
    const position = organization.positions[index];
    const ids = position.allEmployees
      ? await fetchAssignedEmployeeIds(organization.owner, position.instanceId)
      : position.employeeIds;

    ids.forEach((id) => {
      employeeIds.push(id);
    });
  }

  return employeeIds;
};

export const submitLicenseRevoke = async (
  payload: ILicenseRevokePayload,
): Promise<IRevokeSubmitResult> => {
  let revoked = 0;
  let failed = 0;
  const seen: Record<number, boolean> = {};

  for (let index = 0; index < payload.organizations.length; index += 1) {
    const employeeIds = await collectRevokeEmployeeIds(
      payload.organizations[index],
    );

    const results = await Promise.all(
      employeeIds.map((employeeId) => {
        if (seen[employeeId]) {
          return Promise.resolve(null);
        }

        seen[employeeId] = true;

        return LicensesService.POST.revoke({ employeeId }).then(
          () => true,
          () => false,
        );
      }),
    );

    results.forEach((ok) => {
      if (ok == null) {
        return;
      }

      if (ok) {
        revoked += 1;
      } else {
        failed += 1;
      }
    });
  }

  return { revoked, failed };
};
